import { h } from 'preact'
import { useState, useCallback } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import Icon from 'components/Icon'
import VideoView from './VideoView'

export default function ParticipantTile({
  participant,
  isLocal,
  isScreenShare,
  isSpeaker,
  className,
  isAdmin,
  onKick,
  onMute
}) {
  const {
    identity,
    name,
    isMuted,
    isVideoEnabled,
    isScreenSharing,
    videoTrack,
    connectionQuality,
  } = participant

  const [showActions, setShowActions] = useState(false)

  const handleKick = useCallback((e) => {
    e.stopPropagation()
    if (onKick) onKick(identity)
    setShowActions(false)
  }, [onKick, identity])

  const handleMute = useCallback((e) => {
    e.stopPropagation()
    if (onMute) onMute(identity)
    setShowActions(false)
  }, [onMute, identity])

  const toggleActions = useCallback((e) => {
    e.stopPropagation()
    setShowActions(s => !s)
  }, [])

  const tileClass = classNames('ParticipantTile', {
    local: isLocal,
    muted: isMuted,
    noVideo: !isVideoEnabled,
    screenShare: isScreenShare,
    speaker: isSpeaker,
    className,
  })

  const qualityClass = classNames('ParticipantTile-qualityDot', {
    excellent: connectionQuality === 'excellent',
    good: connectionQuality === 'good',
    poor: connectionQuality === 'poor',
  })

  return <div className={tileClass}>
    <div className="ParticipantTile-video">
      {isVideoEnabled && videoTrack
        ? <VideoView track={videoTrack} mirror={isLocal && !isScreenShare} />
        : <div className="ParticipantTile-avatar">
          <Icon type="user-list" size="hg" />
        </div>
      }
    </div>

    {/* Admin action menu (top-right corner) */}
    {isAdmin && !isLocal && !isScreenShare && (
      <div className="ParticipantTile-adminActions">
        <button
          className="ParticipantTile-adminBtn"
          onClick={toggleActions}
          title="Participant options"
        >
          ⋮
        </button>
        {showActions && (
          <div className="ParticipantTile-adminMenu">
            {onMute && !isMuted && (
              <button className="ParticipantTile-adminMenuItem" onClick={handleMute}>
                <Icon type="audioOff" size="sm" /> Mute
              </button>
            )}
            {onKick && (
              <button
                className="ParticipantTile-adminMenuItem ParticipantTile-adminMenuItem-danger"
                onClick={handleKick}>
                <Icon type="close" size="sm" /> Remove
              </button>
            )}
          </div>
        )}
      </div>
    )}

    <div className="ParticipantTile-overlay">
      <div className="ParticipantTile-nameBar">
        <span className="ParticipantTile-name">
          {isLocal && !isScreenShare ? 'You' : (name || identity)}
        </span>
        <div className="ParticipantTile-indicators">
          {isMuted && !isScreenShare &&
            <span className="ParticipantTile-indicator" title="Muted">
              <Icon type="audioOff" size="sm" />
            </span>
          }
          {!isVideoEnabled && !isScreenShare &&
            <span className="ParticipantTile-indicator" title="Camera off">
              <Icon type="disabledVideo" size="sm" />
            </span>
          }
          {isScreenSharing && !isScreenShare &&
            <span className="ParticipantTile-indicator ParticipantTile-indicator-screen" title="Sharing screen">
              <Icon type="shareScreen" size="sm" />
            </span>
          }
          {!isScreenShare &&
            <span className={qualityClass} title={connectionQuality}>●</span>
          }
        </div>
      </div>
    </div>
  </div>
}

ParticipantTile.propTypes = {
  participant: PropTypes.object.isRequired,
  isLocal: PropTypes.bool,
  isScreenShare: PropTypes.bool,
  isSpeaker: PropTypes.bool,
  className: PropTypes.string,
  isAdmin: PropTypes.bool,
  onKick: PropTypes.func,
  onMute: PropTypes.func,
}
